import { useCallback } from 'react';
import { captureWorkspaceLayoutPreset } from './captureWorkspaceLayoutPreset';
import { applyWorkspaceLayoutPreset } from './applyWorkspaceLayoutPreset';
import type { WorkspaceLayoutPreset } from 'generated/graphql';
import { useRecoilCallback } from 'recoil';
import { activeWorkspaceLayoutPresetState, workspaceDisplayArrangementState } from './state';
import { buildDisplayArrangementKeyFromDisplaySlots } from './utils';
import { useToasterDispatch } from 'common/ui/Toaster';
import { logger } from 'modules/logger';

export function useWorkspaceLayoutManager() {
  const { enqueueToast } = useToasterDispatch();

  const capture = useCallback(async () => {
    try {
      return await captureWorkspaceLayoutPreset();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      logger.error(
        `[WorkspaceLayoutManager] Failed to capture workspace layout preset: ${errorMessage}`,
        error
      );
      enqueueToast(`Unable to capture workspace layout preset. ${errorMessage}`, {
        severity: 'error',
      });

      return undefined;
    }
  }, [enqueueToast]);

  const apply = useCallback(
    (preset: Pick<WorkspaceLayoutPreset, 'id' | 'name' | 'windows'>) =>
      applyWorkspaceLayoutPreset(preset),
    []
  );

  // Only the auto-apply owner window applies, so picking a layout just records the request
  const select = useRecoilCallback(
    ({ snapshot, set }) =>
      (
        preset: Pick<WorkspaceLayoutPreset, 'id' | 'name' | 'windows' | 'displayArrangementKey'>
      ) => {
        const displays = snapshot
          .getLoadable(workspaceDisplayArrangementState)
          .getValue()?.displays;

        set(activeWorkspaceLayoutPresetState, {
          presetId: preset.id,
          // The setup it's picked on, or the layout's own until the displays are known
          displayArrangementKey:
            displays != null
              ? buildDisplayArrangementKeyFromDisplaySlots(displays)
              : preset.displayArrangementKey,
          requestedAt: Date.now(),
        });
      },
    []
  );

  return {
    captureWorkspaceLayoutPreset: capture,
    applyWorkspaceLayoutPreset: apply,
    selectWorkspaceLayoutPreset: select,
  };
}
